import { customerDisplayName } from '@folio/utils/customerDisplayName';
import type {
  CustomerDisplay,
  DerogationWithPolicyAndCustomerEntity,
  KorintModulesProductsConfig,
} from 'korint-sdk';
import { type PolicyBranchesForBrokerEntity } from 'korint-sdk';
import {
  AWAITING_FIRST_PAYMENT,
  DEROGATION_APPROVED,
  type KorintConfig,
  type KorintModulesConfig,
  STOP_SCHEDULED,
} from 'korint-sdk/react';
import { config } from '../../config';
import { policyStatuses } from './configs/constants';
import { defaultErrorMessage, errorDictionary } from './configs/errors';
import { uniqAppConfig } from './configs/uniq.config';
import { AxeceMultiRisquesProConfig, AxeceVtcConfig } from './products';
import dayjs from 'dayjs';

const productsConfig = {
  axeceVtc: AxeceVtcConfig,
  axeceMultiRisquesPro: AxeceMultiRisquesProConfig,
} satisfies Record<string, KorintModulesProductsConfig>;

const korintModulesConfig = {
  policyholderContactsModuleConfig: {
    displayBrokerageFirmContact: true,
  },
  intermediaryUserManagement: {
    showTeamManagement: [],
  },
  intermediaryUserCreation: {
    inviteRole: {
      WHOLESALE: ['WHOLESALE_BROKER'],
      RETAIL: ['RETAIL_BROKER'],
    },
  },
  intermediaryGeneralModule: {
    displayInscriptionLink: true,
  },
  intermediaryDetailsLayout: {
    buildBrokerTitle: ({ companyInfo, customFields }) =>
      companyInfo?.name ||
      [customFields.ceoFirstName, customFields.ceoLastName].filter(Boolean).join(' ') ||
      undefined,
  },
  distributionRequestListModule: {
    intermediaryAdditionalInformationFields: [
      {
        name: 'minCommission',
        type: 'number',
        required: true,
        DEPRECATED_placeholder: '10',
        apiDataAccessor: 'minCommission',
      },
      {
        name: 'maxCommission',
        type: 'number',
        required: true,
        DEPRECATED_placeholder: '15',
        apiDataAccessor: 'maxCommission',
      },
      {
        name: 'maxManagementFee',
        type: 'number',
        required: true,
        DEPRECATED_placeholder: '200',
        apiDataAccessor: 'maxManagementFee',
      },
      {
        name: 'commercialRate',
        type: 'number',
        required: false,
        min: 0,
        max: 10,
        isCustomField: true,
      },
      {
        name: 'minTotalCommission',
        type: 'number',
        required: false,
        apiDataAccessor: 'minTotalCommission',
        hidden: true,
      },
      {
        name: 'maxTotalCommission',
        type: 'number',
        required: false,
        apiDataAccessor: 'maxTotalCommission',
        hidden: true,
      },
    ],
  },
  ongoingNewBusiness: {
    filtersConfig: {
      policyStatus: {
        type: 'select',
        options: ['POLICY_CREATED', 'POLICY_QUOTED', DEROGATION_APPROVED, 'POLICY_CONFIRMED'],
      },
      brokerageFirmName: {
        type: 'textInput',
      },
      assignedBroker: {
        type: 'assignedBrokerSelect',
      },
      productIds: {
        type: 'select',
        options: Object.keys(productsConfig),
      },
      creationDate: {
        type: 'date',
      },
    },
    baseFilters: { viewType: 'newBusiness' },
    initialFilters: [
      {
        field: 'creationDate',
        value: {
          start: dayjs().subtract(90, 'day').format('YYYY-MM-DD'),
        },
      },
    ],
    expandCustomers: true,
    expandBrokerageFirm: true,
    columns: [
      {
        name: 'name',
        type: 'string',
        accessor: ({ customers, policy }: PolicyBranchesForBrokerEntity) => {
          return customerDisplayName(
            customers ?? [],
            productsConfig[policy.productId as keyof typeof productsConfig]?.customerInformations
              ?.fieldsConfig,
          );
        },
      },
      {
        name: 'hasAccount',
        type: 'boolean',
        path: 'policy.hasOpenedClientAccount',
      },
      {
        name: 'author',
        type: 'string',
        accessor: ({ policy }: PolicyBranchesForBrokerEntity) =>
          policy.author?.email ||
          (policy.author?.firstName &&
            policy.author?.lastName &&
            `${policy.author?.firstName} ${policy.author?.lastName}`),
      },
      {
        name: 'brokerageFirm',
        type: 'string',
        accessor: ({ brokerageFirm }: PolicyBranchesForBrokerEntity) =>
          brokerageFirm?.companyInfo?.name,
      },
      {
        name: 'status',
        type: 'status',
        path: 'policy.status',
      },
      {
        name: 'allClientDocumentsOk',
        type: 'boolean',
        customField: 'policy',
      },
      {
        name: 'quote',
        type: 'price',
        path: 'policy.totalPremium.amountWithFeesAndTaxes',
      },
      {
        name: 'startDate',
        type: 'date',
        format: 'datetime',
        path: 'branch.createdAt',
      },
      {
        name: 'externalId',
        type: 'string',
        path: 'policy.externalId',
      },
      {
        name: 'productId',
        type: 'product',
        path: 'policy.productId',
      },
    ],
  },
  portfolio: {
    filtersConfig: {
      policyStatus: {
        type: 'select',
        options: [
          'POLICY_SIGNED',
          'POLICY_STARTED',
          'POLICY_STOPPED',
          'POLICY_SUSPENDED',
          STOP_SCHEDULED,
          AWAITING_FIRST_PAYMENT,
        ],
      },
      endDate: {
        type: 'date',
      },
      creationDate: {
        type: 'date',
      },
      complianceAlerts: {
        type: 'checkbox',
      },
      brokerageFirmName: {
        type: 'textInput',
      },
      assignedBroker: {
        type: 'assignedBrokerSelect',
      },
      productIds: {
        type: 'select',
        options: Object.keys(productsConfig),
      },
    },
    baseFilters: { viewType: 'live' },
    expandCustomers: true,
    expandBrokerageFirm: true,
    columns: [
      {
        name: 'name',
        type: 'string',
        accessor: ({ customers, policy }: PolicyBranchesForBrokerEntity) => {
          return customerDisplayName(
            customers ?? [],
            productsConfig[policy.productId as keyof typeof productsConfig]?.customerInformations
              ?.fieldsConfig,
          );
        },
      },
      {
        name: 'hasAccount',
        type: 'boolean',
        path: 'policy.hasOpenedClientAccount',
      },
      {
        name: 'author',
        type: 'string',
        accessor: ({ policy }: PolicyBranchesForBrokerEntity) =>
          policy.author?.email ||
          (policy.author?.firstName &&
            policy.author?.lastName &&
            `${policy.author?.firstName} ${policy.author?.lastName}`),
      },
      {
        name: 'brokerageFirm',
        type: 'string',
        accessor: ({ brokerageFirm }: PolicyBranchesForBrokerEntity) =>
          brokerageFirm?.companyInfo?.name,
      },
      {
        name: 'status',
        type: 'status',
        path: 'policy.status',
      },
      {
        name: 'allClientDocumentsOk',
        type: 'boolean',
        customField: 'policy',
      },
      {
        name: 'quote',
        type: 'price',
        path: 'policy.totalPremium.amountWithFeesAndTaxes',
      },
      {
        name: 'startDate',
        type: 'date',
        format: 'datetime',
        path: 'policy.startedAt',
      },
      {
        name: 'externalId',
        type: 'string',
        path: 'policy.externalId',
      },
      {
        name: 'productId',
        type: 'product',
        path: 'policy.productId',
      },
    ],
  },
  accountingDashboard: {
    filtersConfig: {
      policyStatus: {
        type: 'select',
        options: ['POLICY_SIGNED', 'POLICY_STARTED', 'POLICY_STOPPED', 'POLICY_SUSPENDED'],
      },
      terminationReason: {
        type: 'select',
        options: [
          'VOID',
          'RISK_AGGRAVATION',
          'AMICABLE',
          'ACTIVITY_CESSATION',
          'COMPANY_CHANGE',
          'DEATH',
          'MISSING_DOCUMENTS',
          'DESTRUCTION',
          'MAIN_DUE_DATE',
          'FALSE_DECLARATION',
          'CHATEL_LAW',
          'HAMON_LAW',
          'NON_PAYMENT',
          'RADIATION',
          'CONTRACT_REPLACEMENT',
          'COMPANY_TERMINATION',
          'VEHICLE_RETURN',
          'LICENSE_WITHDRAWAL',
          'NO_EFFECT',
          'CLAIMS',
          'SALE',
          'THEFT',
          'OTHER',
          'REFUSAL_TO_INCREASE_PREMIUM',
          'WAIVER',
        ],
      },
    },
  },
  derogationValidations: {
    filtersConfig: {
      derogationStatus: {
        type: 'select' as const,
        options: ['CREATED', 'APPROVED', 'DENIED'],
      },
      productIds: {
        type: 'select' as const,
        options: Object.keys(productsConfig),
      },
    },
    baseFilters: {},
    columns: [
      {
        name: 'name',
        type: 'string',
        accessor: ({ customers, policy }: DerogationWithPolicyAndCustomerEntity) =>
          customerDisplayName(
            customers,
            productsConfig[policy.productId as keyof typeof productsConfig]?.customerInformations
              ?.fieldsConfig,
          ),
      },
      {
        name: 'companyName',
        type: 'string',
        accessor: ({ brokerageFirm }: DerogationWithPolicyAndCustomerEntity) =>
          brokerageFirm?.companyInfo?.name,
      },
      {
        name: 'derogationAction',
        type: 'status',
        translationNamespace: 'derogationAction',
        accessor: ({ branch }: DerogationWithPolicyAndCustomerEntity) =>
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'DENIED')
            ?.action ??
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'CREATED')
            ?.action ??
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'APPROVED')
            ?.action ??
          'SIGN',
      },
      {
        name: 'status',
        type: 'status',
        translationNamespace: 'derogationStatus',
        accessor: ({ branch }: DerogationWithPolicyAndCustomerEntity) =>
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'DENIED')
            ?.status ??
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'CREATED')
            ?.status ??
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'APPROVED')
            ?.status,
      },
      {
        name: 'createdAt',
        type: 'date',
        format: 'datetime',
        accessor: ({ branch }) =>
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'DENIED')
            ?.createdAt ??
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'CREATED')
            ?.createdAt ??
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'APPROVED')
            ?.createdAt,
      },
      {
        name: 'productId',
        type: 'product',
        path: 'policy.productId',
      },
    ],
  },

  methodPayment: {
    stripePublishableKey: config.STRIPE_PUBLISHABLE_KEY,
  },
  policyLifecycleSummaryModuleConfig: {
    dateDisplayFormat: 'DD/MM/YYYY HH:mm',
  },
  businessRolesConfig: {
    WHOLESALE_BROKER: {
      redirectRules: {
        default: () => '/brokerage-firm-list',
      },
    },
    RETAIL_BROKER: {
      redirectRules: {
        hasUserProducts: '/portfolio',
        hasPendingDistributionRequest: '/waiting',
        noDistributionRequest: (brokerageFirmId: string) =>
          `/brokerage_firm_bCjgcgQCwiyTAiWhNVfFp/brokerage-firm/${brokerageFirmId}/information`,
        default: () => '/ongoing-new-business',
      },
    },
    DRIVER: {
      redirectRules: {
        default: policyId => `/policyholder/${policyId}`,
      },
    },
    SUPER_ADMIN: {
      redirectRules: {
        default: () => '/brokerage-firm-list',
      },
    },
  },
  complianceModuleConfig: {
    enableManualAlert: false,
  },
  documents: {
    uploadModes: {
      policyholderDocuments: 'batch',
      nonDelegatedDocuments: 'category',
      brokerageFirmDocuments: 'batch',
    },
  },
  aiClassification: {
    interfaces: {
      policyholderDashboard: {
        enabled: true,
        showQualityFlags: true,
        qualityFlagsToShow: [
          'corrupted_document',
          'multiple_pages',
          'poor_image_quality',
          'document_oriented_incorrectly',
          'partially_visible',
          'language_mismatch',
          'handwritten_sections',
        ],
        showReclassificationEvents: false,
        showJudgeVerdicts: false,
      },
      brokerageFirmDocuments: {
        enabled: true,
        showQualityFlags: true,
        qualityFlagsToShow: [
          'poor_image_quality',
          'document_oriented_incorrectly',
          'partially_visible',
          'corrupted_document',
          'multiple_pages',
          'language_mismatch',
          'handwritten_sections',
        ],
        showReclassificationEvents: false,
        showJudgeVerdicts: false,
        productId: 'axeceVtc',
      },
    },
  },
} satisfies KorintModulesConfig;

const korintConfig = {
  uniqAppConfig,
  modulesConfig: korintModulesConfig,
  policyStatuses,
  dataAccessors: {
    customerDisplayName: (customer: CustomerDisplay) => {
      if (!customer) return null;
      if (customer.companyName) return customer.companyName;
      return [customer.firstName, customer.lastName].filter(value => value).join(' ');
    },
  },
  productsConfig,
  errorsConfig: {
    defaultErrorMessage,
    errorDictionary,
  },
  supportLinkRoles: ['WHOLESALE_BROKER'],
} as const satisfies KorintConfig;

export default korintConfig;
